import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Swiper as SwiperType } from 'swiper/types';
import { Swiper, SwiperSlide } from 'swiper/react';
import 'swiper/css';

import { createSelfbookHandler } from '../../../utils/selfbook';
import { NiceLink } from '../../shared/NiceLink/NiceLink';
import { PropertyPanelsProps } from './PropertyPanels';
import { images } from '../../shared/assets';
import './AccommodationsPanel.css';

type AccommodationVilla = {
  title: string;
  description: string;
  detailsPath: string;
  images: string[];
};

const AccommodationCard: React.FC<{ villa: AccommodationVilla, controlColor: string, property: string }> = ({ villa, controlColor, property }) => {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  const navigate = useNavigate();
  const handleBooking = createSelfbookHandler(navigate, `/${property}/contact/`);

  const handlePrev = () => {
    setCurrentImageIndex((prevIndex) => (prevIndex - 1 + villa.images.length) % villa.images.length);
  };

  const handleNext = () => {
    setCurrentImageIndex((prevIndex) => (prevIndex + 1) % villa.images.length);
  };

  const { t } = useTranslation();

  return (
    <div className="accommodation-card">
      <div className="accommodation-card__media">
        <div className="accommodation-card__image-list">
          {villa.images.map((image, index) => (
            <img key={index} src={image} alt={villa.title} className={`accommodation-card__image ${index === currentImageIndex ? 'accommodation-card__image--is-active' : ''}`} />
          ))}
        </div>
        <div className="accommodation-card__control-list" style={{ backgroundColor: controlColor }}>
          <button className="accommodation-card__control-item accommodation-card__control-item--prev" onClick={handlePrev}>
            <svg width="5" height="10" viewBox="0 0 5 10" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M0 10V0L5 5L0 10Z" fill="#F3F3F3" />
            </svg>
          </button>
          <button className="accommodation-card__control-item accommodation-card__control-item--next" onClick={handleNext}>
            <svg width="5" height="10" viewBox="0 0 5 10" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M0 10V0L5 5L0 10Z" fill="#F3F3F3" />
            </svg>
          </button>
        </div>
      </div>
      <div className="accommodation-card__text">
        <h3 className='accommodation-card__heading'>{villa.title}</h3>
        <p className='accommodation-card__description'>{villa.description}</p>
        <div className="accommodation-card__links">
          <NiceLink
            text={t('common:shared:accommodation.bookYourStay')}
            color="awasi"
            onClick={handleBooking}
            className="accommodation-card__link accommodation-card__link--primary"
          />
          {/* <CTALink
              text="READ MORE"
              type="secondary"
              color="awasi"
              onClick={() => navigate(villa.detailsPath)}
              className="accommodation-card__link accommodation-card__link--secondary"
            /> */}
        </div>
      </div>
    </div>
  );
};

type AccommodationsData = PropertyPanelsProps & {
  paddingSmall?: boolean;
};

const AccommodationsPanel: React.FC<AccommodationsData> = ({ property, paddingSmall }) => {
  const { t } = useTranslation();
  const swiperRef = useRef<SwiperType | null>(null);

  const handleArrowClick = (direction: 'left' | 'right') => {
    if (swiperRef.current) {
      if (direction === 'left') {
        swiperRef.current?.slidePrev();
      } else {
        swiperRef.current?.slideNext();
      }
    }
  };

  // Get property-specific content
  const getAccommodationsForProperty = () => {
    switch (property.toLowerCase()) {
      case 'atacama':
        return {
          controlColor: '#7B6569',
          villas: [
            {
              title: t('properties/atacama:accommodations.rooms.0.title'),
              description: t('properties/atacama:accommodations.rooms.0.description'),
              detailsPath: "/atacama/accommodations/superior-round-suites/",
              images: [
                "/assets/images/villas/atacama/Superior Round Suite/atacama 081.jpg",
                "/assets/images/villas/atacama/Superior Round Suite/atacama 082.jpg",
                "/assets/images/villas/atacama/Superior Round Suite/atacama 085.jpg",
                "/assets/images/villas/atacama/Superior Round Suite/atacama 086.jpg",
                "/assets/images/villas/atacama/Superior Round Suite/atacama 090.jpg",
                "/assets/images/villas/atacama/Superior Round Suite/atacama 093.jpg",
                "/assets/images/villas/atacama/Superior Round Suite/atacama 096.jpg",
                "/assets/images/villas/atacama/Superior Round Suite/atacama 097.jpg",
                "/assets/images/villas/atacama/Superior Round Suite/atacama 099.jpg"
              ]
            },
            {
              title: t('properties/atacama:accommodations.rooms.1.title'),
              description: t('properties/atacama:accommodations.rooms.1.description'),
              detailsPath: "/atacama/accommodations/round-rooms/",
              images: [
                "/assets/images/villas/atacama/Round Room/a-rr (1).jpg",
                "/assets/images/villas/atacama/Round Room/a-rr (2).jpg",
                "/assets/images/villas/atacama/Round Room/a-rr (3).jpg",
                "/assets/images/villas/atacama/Round Room/a-rr (4).jpg",
                "/assets/images/villas/atacama/Round Room/a-rr (5).jpg"
              ]
            }
          ]
        };
      case 'iguazu':
        return {
          controlColor: '#3E5B58',
          villas: [
            {
              title: t('properties/iguazu:accommodations.rooms.0.title'),
              description: t('properties/iguazu:accommodations.rooms.0.description'),
              detailsPath: "/iguazu/accommodations/13-villas/",
              images: [
                "/assets/images/villas/iguazu/Villa/standard villa  004.jpg",
                "/assets/images/villas/iguazu/Villa/standard villa  003.jpg",
                "/assets/images/villas/iguazu/Villa/standard villa  008.jpg",
                "/assets/images/villas/iguazu/Villa/standard villa  009.jpg",
                "/assets/images/villas/iguazu/Villa/standard villa  010.jpg",
                "/assets/images/villas/iguazu/Villa/standard villa  011.jpg",
              ]
            },
            {
              title: t('properties/iguazu:accommodations.rooms.1.title'),
              description: t('properties/iguazu:accommodations.rooms.1.description'),
              detailsPath: "/iguazu/accommodations/master-villa/",
              images: [
                "/assets/images/villas/iguazu/Master Villa/master villa  018.jpg",
                "/assets/images/villas/iguazu/Master Villa/master villa  001.jpg",
                "/assets/images/villas/iguazu/Master Villa/master villa  006.jpg",
                "/assets/images/villas/iguazu/Master Villa/master villa  007.jpg",
                "/assets/images/villas/iguazu/Master Villa/master villa  008.jpg",
                "/assets/images/villas/iguazu/Master Villa/master villa  009.jpg",
                "/assets/images/villas/iguazu/Master Villa/master villa  013.jpg",
                "/assets/images/villas/iguazu/Master Villa/master villa  014.jpg",
                "/assets/images/villas/iguazu/Master Villa/master villa  015.jpg",
                "/assets/images/villas/iguazu/Master Villa/master villa  016.jpg",
                "/assets/images/villas/iguazu/Master Villa/master villa  017.jpg",
              ]
            }
          ]
        };
      case 'mendoza':
        return {
          controlColor: '#502630',
          villas: [
            {
              title: t('properties/mendoza:villaCarousel.cards.vineyard.title'),
              description: t('properties/mendoza:villaCarousel.cards.vineyard.excerpt'),
              detailsPath: "/mendoza/accommodations/vineyard-villa/",
              images: [
                "/assets/images/villas/mendoza/Vineyard Villa/m_vv (5).jpg",
                "/assets/images/villas/mendoza/Vineyard Villa/m_vv (1).jpg",
                "/assets/images/villas/mendoza/Vineyard Villa/m_vv (2).jpg",
                "/assets/images/villas/mendoza/Vineyard Villa/m_vv (3).jpg",
                "/assets/images/villas/mendoza/Vineyard Villa/m_vv (4).jpg",
                "/assets/images/villas/mendoza/Vineyard Villa/m_vv (6).jpg",
                "/assets/images/villas/mendoza/Vineyard Villa/m_vv (7).jpg",
                "/assets/images/villas/mendoza/Vineyard Villa/m_vv (8).jpg",
                "/assets/images/villas/mendoza/Vineyard Villa/m_vv (9).jpg",
                "/assets/images/villas/mendoza/Vineyard Villa/m_vv (10).jpg",
              ]
            },
            {
              title: t('properties/mendoza:villaCarousel.cards.twoBedroom.title'),
              description: t('properties/mendoza:villaCarousel.cards.twoBedroom.excerpt'),
              detailsPath: "/mendoza/accommodations/two-bedroom-villa/",
              images: [
                "/assets/images/villas/mendoza/Two bedroom Villa/m_tbv (6).jpg",
                "/assets/images/villas/mendoza/Two bedroom Villa/m_tbv (1).jpg",
                "/assets/images/villas/mendoza/Two bedroom Villa/m_tbv (2).jpg",
                "/assets/images/villas/mendoza/Two bedroom Villa/m_tbv (3).jpg",
                "/assets/images/villas/mendoza/Two bedroom Villa/m_tbv (4).jpg",
                "/assets/images/villas/mendoza/Two bedroom Villa/m_tbv (5).jpg",
                "/assets/images/villas/mendoza/Two bedroom Villa/m_tbv (7).jpg",
              ]
            },
            {
              title: t('properties/mendoza:villaCarousel.cards.grandVilla.title'),
              description: t('properties/mendoza:villaCarousel.cards.grandVilla.excerpt'),
              detailsPath: "/mendoza/accommodations/grand-villa/",
              images: [
                "/assets/images/villas/mendoza/Grand Villa/gv (3).JPG",
                "/assets/images/villas/mendoza/Grand Villa/gv (2).JPG",
                "/assets/images/villas/mendoza/Grand Villa/gv (1).jpg",
                "/assets/images/villas/mendoza/Grand Villa/gv (4).JPG",
                "/assets/images/villas/mendoza/Grand Villa/gv (5).jpg",
              ]
            }
          ]
        };
      case 'santa-catarina':
      case 'santacatarina':
        return {
          controlColor: '#009485',
          villas: [
            {
              title: t('properties/santa-catarina:villaCards.arvoredo.title'),
              description: t('properties/santa-catarina:villaCards.arvoredo.description'),
              detailsPath: "/santa-catarina/accommodations/villa-arvoredo/",
              images: [
                "/assets/images/villas/santa-catarina/Villa Arvoredo/ARVOREDO_BANGALO (1).jpg",
                "/assets/images/villas/santa-catarina/Villa Arvoredo/ARVOREDO_BANGALO (2).jpg",
                "/assets/images/villas/santa-catarina/Villa Arvoredo/ARVOREDO_BANGALO (3).jpg",
                "/assets/images/villas/santa-catarina/Villa Arvoredo/ARVOREDO_BANGALO (4).jpg",
              ]
            },
            {
              title: t('properties/santa-catarina:villaCards.pontaDosGanchos.title'),
              description: t('properties/santa-catarina:villaCards.pontaDosGanchos.description'),
              detailsPath: "/santa-catarina/accommodations/villa-ponta-dos-ganchos/",
              images: [
                "/assets/images/villas/santa-catarina/Villa ponta dos ganchos/sc_vpdg (1).jpg",
                "/assets/images/villas/santa-catarina/Villa ponta dos ganchos/sc_vpdg (2).jpg",
                "/assets/images/villas/santa-catarina/Villa ponta dos ganchos/sc_vpdg (3).jpg",
                "/assets/images/villas/santa-catarina/Villa ponta dos ganchos/sc_vpdg (4).jpg",
                "/assets/images/villas/santa-catarina/Villa ponta dos ganchos/sc_vpdg (5).jpg",
                "/assets/images/villas/santa-catarina/Villa ponta dos ganchos/sc_vpdg (6).jpg",
                "/assets/images/villas/santa-catarina/Villa ponta dos ganchos/sc_vpdg (7).jpg",
                "/assets/images/villas/santa-catarina/Villa ponta dos ganchos/sc_vpdg (8).jpg",
              ]
            },
            {
              title: t('properties/santa-catarina:villaCards.costaEsmeralda.title'),
              description: t('properties/santa-catarina:villaCards.costaEsmeralda.description'),
              detailsPath: "/santa-catarina/accommodations/villa-costa-esmeralda/",
              images: [
                "/assets/images/villas/santa-catarina/Villa Costa Esmeralda/sc_vce (6).jpg",
                "/assets/images/villas/santa-catarina/Villa Costa Esmeralda/sc_vce (1).jpg",
                "/assets/images/villas/santa-catarina/Villa Costa Esmeralda/sc_vce (2).jpg",
                "/assets/images/villas/santa-catarina/Villa Costa Esmeralda/sc_vce (3).jpg",
                "/assets/images/villas/santa-catarina/Villa Costa Esmeralda/sc_vce (4).jpg",
                "/assets/images/villas/santa-catarina/Villa Costa Esmeralda/sc_vce (5).jpg",
              ]
            },
            {
              title: t('properties/santa-catarina:villaCards.anhatomirim.title'),
              description: t('properties/santa-catarina:villaCards.anhatomirim.description'),
              detailsPath: "/santa-catarina/accommodations/villa-anhatomirim/",
              images: [
                "/assets/images/villas/santa-catarina/Villa Anhatomirim/VILLA_ANHATOMIRIM (1).jpg",
                "/assets/images/villas/santa-catarina/Villa Anhatomirim/VILLA_ANHATOMIRIM (1).png",
                "/assets/images/villas/santa-catarina/Villa Anhatomirim/VILLA_ANHATOMIRIM (2).jpg",
                "/assets/images/villas/santa-catarina/Villa Anhatomirim/VILLA_ANHATOMIRIM (3).jpg",
                "/assets/images/villas/santa-catarina/Villa Anhatomirim/VILLA_ANHATOMIRIM (4).jpg",
                "/assets/images/villas/santa-catarina/Villa Anhatomirim/VILLA_ANHATOMIRIM (5).jpg",
              ]
            },
            {
              title: t('properties/santa-catarina:villaCards.sissial.title'),
              description: t('properties/santa-catarina:villaCards.sissial.description'),
              detailsPath: "/santa-catarina/accommodations/villa-sissial/",
              images: [
                "/assets/images/villas/santa-catarina/Villa Sissial/VILLA_SISSIAL (6).jpg",
                "/assets/images/villas/santa-catarina/Villa Sissial/VILLA_SISSIAL (1).jpg",
                "/assets/images/villas/santa-catarina/Villa Sissial/VILLA_SISSIAL (2).jpg",
                "/assets/images/villas/santa-catarina/Villa Sissial/VILLA_SISSIAL (3).jpg",
                "/assets/images/villas/santa-catarina/Villa Sissial/VILLA_SISSIAL (4).jpg",
                "/assets/images/villas/santa-catarina/Villa Sissial/VILLA_SISSIAL (5).jpg",
              ]
            },
            {
              title: t('properties/santa-catarina:villaCards.tingua.title'),
              description: t('properties/santa-catarina:villaCards.tingua.description'),
              detailsPath: "/santa-catarina/accommodations/villa-tingua/",
              images: [
                "/assets/images/villas/santa-catarina/Villa Tingua/Villa_Tingua (3).jpg",
                "/assets/images/villas/santa-catarina/Villa Tingua/Villa_Tingua (1).jpg",
                "/assets/images/villas/santa-catarina/Villa Tingua/Villa_Tingua (1).png",
                "/assets/images/villas/santa-catarina/Villa Tingua/Villa_Tingua (2).jpg",
                "/assets/images/villas/santa-catarina/Villa Tingua/Villa_Tingua (4).jpg",
                "/assets/images/villas/santa-catarina/Villa Tingua/Villa_Tingua (5).jpg",
              ]
            },

          ]
        };
      case 'patagonia':
      default:
        return {
          controlColor: '#9FA9AD',
          villas: [
            {
              title: t('properties/patagonia:accommodations.rooms.0.title'),
              description: t('properties/patagonia:accommodations.rooms.0.description'),
              detailsPath: "/patagonia/accommodations/13-villas/",
              images: [
                "/assets/images/villas/patagonia/Villa/p_v (8).jpg",
                "/assets/images/villas/patagonia/Villa/p_v (1).JPG",
                "/assets/images/villas/patagonia/Villa/p_v (9).JPG",
                "/assets/images/villas/patagonia/Villa/p_v (3).jpg",
                "/assets/images/villas/patagonia/Villa/p_v (4).jpg",
                "/assets/images/villas/patagonia/Villa/p_v (5).jpg",
                "/assets/images/villas/patagonia/Villa/p_v (6).jpg",
                "/assets/images/villas/patagonia/Villa/p_v (7).jpg",
                "/assets/images/villas/patagonia/Villa/p_v (8).jpg",
              ]
            },
            {
              title: t('properties/patagonia:accommodations.rooms.1.title'),
              description: t('properties/patagonia:accommodations.rooms.1.description'),
              detailsPath: "/patagonia/accommodations/master-villa/",
              images: [
                "/assets/images/villas/patagonia/Master Villa/p_mv (4).jpg",
                "/assets/images/villas/patagonia/Master Villa/p_mv (1).jpg",
                "/assets/images/villas/patagonia/Master Villa/p_mv (2).jpg",
                "/assets/images/villas/patagonia/Master Villa/p_mv (3).jpg",
                "/assets/images/villas/patagonia/Master Villa/p_mv (5).jpg",
                "/assets/images/villas/patagonia/Master Villa/p_mv (6).jpg",
                "/assets/images/villas/patagonia/Master Villa/p_mv (7).jpg",
                "/assets/images/villas/patagonia/Master Villa/p_mv (8).jpg",
                "/assets/images/villas/patagonia/Master Villa/p_mv (9).jpg",
              ]
            }
          ]
        };
    }
  };

  const accommodations = getAccommodationsForProperty();

  return (
    <div className={`accommodations-panel ${paddingSmall && 'accommodations-panel-padding-small'}`}>
      <Swiper
        freeMode
        spaceBetween={20}
        slidesPerView={1}
        className='accommodations-panel-carousel-swiper'
        breakpoints={{
          1024: {
            slidesPerView: 2,
            spaceBetween: 30,
          },
        }}
        onSwiper={(currentSwiper: SwiperType) => swiperRef.current = currentSwiper}
      >
        {accommodations.villas.map((villa, index) => (
          <SwiperSlide key={`accommodation-${index}`}>
            <AccommodationCard
              villa={villa as AccommodationVilla}
              controlColor={accommodations.controlColor}
              property={property}
            />
          </SwiperSlide>
        ))}
      </Swiper>

      {accommodations.villas.length > 2 && <>
        <div className="glacier-video-exploration__arrow left" onClick={() => handleArrowClick('left')}>
          <img
            src={images.carouselArrow}
            alt="Scroll to see more destinations"
            className="glacier-video-exploration__arrow-image"
          />
        </div>

        <div className="glacier-video-exploration__arrow right" onClick={() => handleArrowClick('right')}>
          <img
            src={images.carouselArrow}
            alt="Scroll to see more destinations"
            className="glacier-video-exploration__arrow-image"
          />
        </div>
      </>}
    </div>
  );
};

export default AccommodationsPanel;